<template>
  <div class="ac" ref="wrap" @mousemove="onMove" @mouseleave="hover = -1">
    <div class="ac-legend">
      <span class="ac-key"><i class="ac-swatch ac-swatch--solid"></i>{{ seriesLabel }}</span>
      <span class="ac-key" v-if="compare && compare.length"><i class="ac-swatch ac-swatch--dashed"></i>{{ compareLabel }}</span>
      <span class="ac-key ac-key--pending" v-else-if="comparePending"><i class="ac-swatch ac-swatch--dashed"></i>{{ compareLabel }} · loading</span>
    </div>

    <svg class="ac-svg" :width="w" :height="height" :viewBox="`0 0 ${w} ${height}`" role="img" :aria-label="ariaLabel">
      <!-- grid: three quiet lines, labelled on the left -->
      <g class="ac-grid">
        <line v-for="g in grid" :key="'g' + g.y" :x1="padL" :x2="w - padR" :y1="g.y" :y2="g.y" />
        <text v-for="g in grid" :key="'t' + g.y" :x="padL - 6" :y="g.y + 4" text-anchor="end">{{ g.label }}</text>
      </g>

      <!-- comparison: the same days last time, dashed and quieter -->
      <path v-if="comparePath" class="ac-compare" :d="comparePath" />

      <!-- this series: filled, stroked, drawn in -->
      <path v-if="areaPath" class="ac-fill" :d="areaPath" />
      <path v-if="linePath" class="ac-line" :d="linePath" pathLength="1" :key="'line' + drawKey" />

      <!-- hover -->
      <g v-if="hover >= 0 && pts[hover]">
        <line class="ac-cross" :x1="pts[hover][0]" :x2="pts[hover][0]" :y1="padT" :y2="height - padB" />
        <circle class="ac-dot ac-dot--compare" v-if="cpts[hover]" :cx="cpts[hover][0]" :cy="cpts[hover][1]" r="3.5" />
        <circle class="ac-dot" :cx="pts[hover][0]" :cy="pts[hover][1]" r="4" />
      </g>
      <!-- the end of the line says its value out loud -->
      <g v-else-if="lastPt">
        <circle class="ac-dot" :cx="lastPt[0]" :cy="lastPt[1]" r="3.5" />
        <text class="ac-end" :x="lastPt[0] - 6" :y="lastPt[1] - 8" text-anchor="end">{{ fmt(series[series.length - 1]) }}</text>
      </g>

      <g class="ac-x">
        <text v-for="(l, i) in xLabels" :key="'x' + i" :x="l.x" :y="height - 6" text-anchor="middle">{{ l.text }}</text>
      </g>
    </svg>

    <div class="ac-tip" v-if="hover >= 0 && pts[hover]" :style="tipStyle">
      <div class="ac-tip-title">{{ labels[hover] || "" }}</div>
      <div class="ac-tip-row"><i class="ac-swatch ac-swatch--solid"></i>{{ seriesLabel }} <b class="num">{{ fmt(series[hover]) }}</b></div>
      <div class="ac-tip-row" v-if="compare && compare[hover] != null"><i class="ac-swatch ac-swatch--dashed"></i>{{ compareLabel }} <b class="num">{{ fmt(compare[hover]) }}</b></div>
      <div class="ac-tip-delta num" v-if="compare && compare[hover]" :class="deltaClass(hover)">{{ deltaText(hover) }}</div>
    </div>
  </div>
</template>

<script>
import { niceScale } from "@/views/Analytics/utils";
import { trf } from "@/i18n";

/**
 * Monotone cubic interpolation (Fritsch–Carlson). Unlike a Catmull-Rom spline it
 * never overshoots: a series that goes 0, 0, 34 draws a curve that stays at the
 * baseline until it rises, instead of dipping below zero first — a sales line
 * under the axis reads as a refund that never happened.
 */
function monotonePath(pts) {
  const n = pts.length;
  if (n < 2) return "";
  const xs = pts.map((p) => p[0]), ys = pts.map((p) => p[1]);
  const dx = [], dy = [], m = [];
  for (let i = 0; i < n - 1; i++) { dx.push(xs[i + 1] - xs[i]); dy.push(ys[i + 1] - ys[i]); m.push(dx[i] ? dy[i] / dx[i] : 0); }
  const t = [m[0]];
  for (let i = 1; i < n - 1; i++) t.push(m[i - 1] * m[i] <= 0 ? 0 : (m[i - 1] + m[i]) / 2);
  t.push(m[n - 2]);
  for (let i = 0; i < n - 1; i++) {
    if (m[i] === 0) { t[i] = 0; t[i + 1] = 0; continue; }
    const a = t[i] / m[i], b = t[i + 1] / m[i], h = Math.hypot(a, b);
    if (h > 3) { t[i] = (3 * a) / h * m[i]; t[i + 1] = (3 * b) / h * m[i]; }
  }
  let d = "";
  for (let i = 0; i < n - 1; i++) {
    const h = dx[i];
    d += " C " + (xs[i] + h / 3) + " " + (ys[i] + (t[i] * h) / 3) + "," + (xs[i + 1] - h / 3) + " " + (ys[i + 1] - (t[i + 1] * h) / 3) + "," + xs[i + 1] + " " + ys[i + 1];
  }
  return d;
}

/**
 * One measure over a run of days, drawn as an area with a smooth line, with
 * the same days from the period before as a dashed line for comparison.
 *
 * Rules it keeps (from the dataviz notes): one axis; two series carry a legend
 * and distinct marks (solid+fill vs dashed) so colour is never the only clue;
 * the last point is labelled directly; grid and axes stay recessive; values in
 * tooltips wear text tokens, not the series colour; the curve is monotone so it
 * never invents a dip or a peak between two days. The line draws in on arrival
 * (400ms) and nothing else moves.
 *
 * Coordinates are real pixels (the container is measured), so nothing is
 * stretched and a hover maps straight onto a point.
 */
export default {
  name: "AreaChart",
  props: {
    series: { type: Array, required: true },
    compare: { type: Array, default: null },
    comparePending: { type: Boolean, default: false },
    labels: { type: Array, default: () => [] },
    seriesLabel: { type: String, default: "This period" },
    compareLabel: { type: String, default: "The period before" },
    height: { type: Number, default: 220 },
    formatValue: { type: Function, default: null },
    ariaLabel: { type: String, default: "Trend chart" },
  },
  data() {
    return { w: 600, hover: -1, drawKey: 0, ro: null };
  },
  computed: {
    padL() { return 52; },
    padR() { return 16; },
    padT() { return 18; },
    padB() { return 26; },
    maxVal() {
      const all = this.series.concat(this.compare || []).map((n) => Number(n) || 0);
      return Math.max(1, ...all);
    },
    scale() { return niceScale(this.maxVal, 3); },
    top() { return (this.scale && this.scale.max) || this.maxVal; },
    grid() {
      const s = this.scale;
      if (!s || !s.ticks) return [];
      return s.ticks.filter((t) => t > 0).map((t) => ({ y: this.sy(t), label: this.fmtShort(t) }));
    },
    pts() { return this.toPoints(this.series); },
    cpts() { return this.compare && this.compare.length > 1 ? this.toPoints(this.compare) : []; },
    linePath() {
      if (this.pts.length < 2) return "";
      return "M " + this.pts[0][0] + " " + this.pts[0][1] + monotonePath(this.pts);
    },
    areaPath() {
      if (this.pts.length < 2) return "";
      const p = this.pts;
      const base = this.height - this.padB;
      return this.linePath + " L " + p[p.length - 1][0] + " " + base + " L " + p[0][0] + " " + base + " Z";
    },
    comparePath() {
      if (this.cpts.length < 2) return "";
      return "M " + this.cpts[0][0] + " " + this.cpts[0][1] + monotonePath(this.cpts);
    },
    lastPt() { return this.pts.length ? this.pts[this.pts.length - 1] : null; },
    xLabels() {
      const n = this.pts.length;
      if (!n) return [];
      // Roughly one label per 60px of chart, so a phone shows every other day
      // instead of seven labels running into each other.
      const room = Math.max(1, Math.floor((this.w - this.padL - this.padR) / 60));
      const every = Math.max(1, Math.ceil(n / room));
      return this.pts.map((p, i) => ({ x: p[0], text: this.labels[i] || "" })).filter((_, i) => i % every === 0);
    },
    tipStyle() {
      const x = this.pts[this.hover] ? this.pts[this.hover][0] : 0;
      const right = x > this.w * 0.6;
      return right ? { right: (this.w - x + 12) + "px" } : { left: (x + 12) + "px" };
    },
  },
  watch: {
    series() { this.drawKey++; },
  },
  mounted() {
    this.measure();
    if (window.ResizeObserver) {
      this.ro = new ResizeObserver(() => this.measure());
      this.ro.observe(this.$refs.wrap);
    } else {
      window.addEventListener("resize", this.measure);
    }
  },
  beforeUnmount() {
    if (this.ro) this.ro.disconnect();
    else window.removeEventListener("resize", this.measure);
  },
  methods: {
    measure() {
      const el = this.$refs.wrap;
      if (el && el.clientWidth) this.w = el.clientWidth;
    },
    sy(v) {
      const h = this.height - this.padT - this.padB;
      return this.padT + h - (Math.max(0, Number(v) || 0) / this.top) * h;
    },
    toPoints(arr) {
      const n = arr.length;
      if (n < 2) return [];
      const cw = this.w - this.padL - this.padR;
      return arr.map((v, i) => [this.padL + (i / (n - 1)) * cw, this.sy(v)]);
    },
    onMove(e) {
      if (!this.pts.length) return;
      const rect = this.$refs.wrap.getBoundingClientRect();
      const x = e.clientX - rect.left;
      let best = 0, bd = Infinity;
      this.pts.forEach((p, i) => { const d = Math.abs(p[0] - x); if (d < bd) { bd = d; best = i; } });
      this.hover = best;
    },
    fmt(v) { return this.formatValue ? this.formatValue(v) : String(Math.round(Number(v) || 0)); },
    fmtShort(v) {
      const n = Number(v) || 0;
      if (this.formatValue) {
        if (n >= 1000) return "$" + (n / 1000).toFixed(n >= 10000 ? 0 : 1) + "k";
        return this.formatValue(n).replace(/\.00$/, "");
      }
      return n >= 1000 ? (n / 1000).toFixed(1) + "k" : String(n);
    },
    deltaText(i) {
      const a = Number(this.series[i]) || 0, b = Number(this.compare[i]) || 0;
      if (!b) return "";
      const p = ((a - b) / b) * 100;
      return trf("{0}% vs {1}", [(p >= 0 ? "+" : "") + p.toFixed(1), this.compareLabel.toLowerCase()]);
    },
    deltaClass(i) {
      const a = Number(this.series[i]) || 0, b = Number(this.compare[i]) || 0;
      return a >= b ? "is-up" : "is-down";
    },
  },
};
</script>

<style scoped>
.ac { position: relative; width: 100%; }
.ac-legend { display: flex; gap: var(--s-4); margin-bottom: var(--s-2); font-size: var(--t-caption); line-height: var(--t-caption-lh); color: var(--text-secondary); }
.ac-key { display: inline-flex; align-items: center; gap: var(--s-2); }
.ac-key--pending { color: var(--text-muted); }
.ac-swatch { display: inline-block; width: 18px; height: 0; border-top: 2px solid var(--chart-series); }
.ac-swatch--dashed { border-top-style: dashed; border-top-color: var(--chart-compare); }
.ac-svg { display: block; width: 100%; overflow: visible; }
.ac-grid line { stroke: var(--border-light); stroke-width: 1; }
.ac-grid text, .ac-x text { fill: var(--text-muted); font-size: 11px; font-family: var(--font-main); }
.ac-fill { fill: var(--chart-series); opacity: 0.14; }
.ac-line { fill: none; stroke: var(--chart-series); stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; }
.ac-compare { fill: none; stroke: var(--chart-compare); stroke-width: 1.5; stroke-dasharray: 5 4; stroke-linecap: round; opacity: 0.9; }
.ac-cross { stroke: var(--border-secondary); stroke-width: 1; stroke-dasharray: 3 3; }
.ac-dot { fill: var(--chart-series); stroke: var(--bg-primary); stroke-width: 2; }
.ac-dot--compare { fill: var(--chart-compare); stroke: var(--bg-primary); stroke-width: 2; }
.ac-end { fill: var(--text-primary); font-size: 12px; font-weight: 600; font-family: var(--font-main); }
.ac-tip { position: absolute; top: 28px; z-index: 5; min-width: 11rem; padding: var(--s-2) var(--s-3); background: var(--bg-primary); border: 1px solid var(--border-primary); border-radius: var(--r-md); box-shadow: var(--shadow-overlay); pointer-events: none; font-size: var(--t-caption); line-height: var(--t-caption-lh); color: var(--text-secondary); }
.ac-tip-title { font-weight: 600; color: var(--text-primary); margin-bottom: 2px; }
.ac-tip-row { display: flex; align-items: center; gap: var(--s-2); }
.ac-tip-row b { margin-left: auto; color: var(--text-primary); }
.ac-tip-delta { margin-top: 2px; font-weight: 600; }
.ac-tip-delta.is-up { color: var(--success); }
.ac-tip-delta.is-down { color: var(--danger); }
</style>
